import { useEnhancedQuery } from "@uplift-ltd/apollo";
import { makeUrl } from "@uplift-ltd/strings";
import { notEmpty } from "@uplift-ltd/ts-helpers";
import { useRouter } from "next/router";
import React from "react";
import { PageIdType } from "__generated__/globalTypes";
import {
  PageQuery,
  PageQueryVariables,
  STATIC_PAGE_QUERY,
} from "components/StaticPageTemplate/queries";
import Container from "components/common/Container";
import HtmlContent from "components/common/HtmlContent";
import { FullPageLoading } from "components/common/Loading";
import SectionHeader from "components/common/SectionHeader";
import TableOfContents from "components/common/TableOfContents";
import { PRIVATE_PRACTICE_TOOLS_URL } from "constants/urls";
import { mapNodes } from "helpers/mapNodes";
import { usePageMeta } from "hooks/usePageMeta";
import ProductCard from "./ProductCard";
import {
  PracticeToolsProductsQuery,
  PracticeToolsProductsVariables,
} from "./__generated__/queries";
import { PRACTICE_TOOLS_PRODUCTS_QUERY } from "./queries";
import styles from "./PrivatePracticeTools.module.css";

export type PrivatePracticeToolsProps = { title: string };

const PrivatePracticeTools = ({ title }: PrivatePracticeToolsProps) => {
  const { pathname } = useRouter();
  const { data: staticPage, loading: pageLoading } = useEnhancedQuery<
    PageQuery,
    PageQueryVariables
  >(STATIC_PAGE_QUERY, {
    variables: { id: pathname || "", idType: PageIdType.URI },
  });

  const { data: products, loading: productsLoading } = useEnhancedQuery<
    PracticeToolsProductsQuery,
    PracticeToolsProductsVariables
  >(PRACTICE_TOOLS_PRODUCTS_QUERY, {
    variables: { category: pathname },
    skip: !pathname,
  });

  usePageMeta(title || staticPage?.page?.title, staticPage?.page?.seo);
  const tocSubjects = mapNodes(products?.products, item => ({
    label: item.name || "",
    href: `#${item.slug}`,
    id: item.slug,
  })).filter(notEmpty);

  if (pageLoading || productsLoading || !staticPage?.page || !products) return <FullPageLoading />;
  const { page } = staticPage;
  return (
    <>
      {page?.title && page?.content && (
        <SectionHeader title={page?.title || ""} summary={page?.content || ""} />
      )}
      <Container>
        <div className={styles.catalogMainWrapper}>
          <div className={styles.catalogSubWrapper}>
            <TableOfContents items={tocSubjects} />
            {mapNodes(
              products.products,
              product =>
                product && (
                  <ProductCard
                    key={product.id}
                    product={product}
                    productType="privatePracticeTool"
                    href={makeUrl(PRIVATE_PRACTICE_TOOLS_URL, { slug: product.slug as string })}
                  >
                    <HtmlContent>
                      {product.privatePracticeTools?.shortDescription || ""}
                    </HtmlContent>
                  </ProductCard>
                )
            )}
          </div>
        </div>
      </Container>
    </>
  );
};

export default PrivatePracticeTools;
